import "./FileUpload.css";

import {
	BATCH_MESSAGE_CONTAINER_NAME,
	IVR_FLOW_CONTAINER_NAME,
} from "../../../common/constants";
import { Chip, Icon, Tooltip } from "../../../common/MaterialComponent";
import {
	DeleteAudioFileAsync,
	GetSASTokenForBlobFile,
} from "../../../api/batchNotificationController";
import React, { useEffect, useState } from "react";

import { ButtonPrimary } from "../../../common/sharedStyle";
import { ConfirmationDialog } from "../../Confirmation/ConfirmationDialog";
import Shared from "../../../common/shared";
import { TemplateType } from "../../../common/enum";
import { useGetComponentStringsContext } from "../../../hooks/useGetComponentStrings";

export interface FileUploadProp {
	message: string;
	audioDuration: number;
	index: string;
	partitionKey: string;
	removeUploadedFile: () => void;
	onFileChange: (
		fileUrl: string,
		fileName: string,
		audioDuration: number
	) => void;
	isReadOnly?: boolean;
	highlightRequiredDetail?: boolean;
	rowKey?: string;
	templateType?: TemplateType;
	fileName?: string;
}

const FileUpload = React.forwardRef((props: FileUploadProp, ref) => {
	const [
		isMaxAllowedDurationError,
		setIsMaxAllowedDurationError,
	] = useState<boolean>(false);
	const [
		isFileTypeExtensionError,
		setIsFileTypeExtensionError,
	] = useState<boolean>(false);
	const [fileName, setFileName] = useState("");
	const [fileUrl, setFileUrl] = useState("");
	const [audioDuration, setAudioDuration] = useState(0);

	const voiceRecordingManualUrl =
		process.env.REACT_APP_VOICE_RECORDING_MANUAL_URL;

	const mimeTypeList = [
		"audio/mp3",
		"audio/mpeg",
		"audio/wav",
		"audio/wave",
		"audio/x-wav",
		"audio/aiff",
		"audio/x-aifc",
		"audio/x-aiff",
		"audio/x-gsm",
		"audio/gsm",
		"audio/ulaw",
	];
	const maxAudioDuration = process.env.REACT_APP_MAX_AUDIO_FILE_DURATION ?? 600;
	const maxAudioDurationInMinutes = Number(maxAudioDuration) / 60;

	const strings: any = useGetComponentStringsContext().FileUpload;
	const maxAudioDurationErrorMessage = `${strings.maxAllowedDurationError} ${maxAudioDurationInMinutes} ${strings.minutesText}`;
	const [openConfirmationDialog, setOpenConfirmationDialog] = useState(false);

	useEffect(() => {
		setFileUrl(props.message);
		setAudioDuration(props.audioDuration);
		setFileName(props.fileName || "");
	}, [props.audioDuration, props.message, props.fileName]);

	const onUploadFileButtonClick = () => {
		if (props.isReadOnly) {
			return;
		}
		setIsMaxAllowedDurationError(false);
		setIsFileTypeExtensionError(false);
		const fileUploadElement: any = document.getElementById(props.index);
		if (fileUploadElement) {
			fileUploadElement.click();
		}
	};

	const onFileChangeHandler = (event: any) => {
		// find input element
		const fileInputElement: any = document.getElementById(props.index);
		const file = event.target.files[0];
		if (file) {
			const fileType = file.type;
			if (mimeTypeList.indexOf(fileType) > -1) {
				const fr = new FileReader();
				fr.readAsDataURL(file);
				fr.addEventListener("load", (event: any) => {
					const url = (fr.result as string).split(",")[1];
					// get duration of audio file in seconds
					const audio = document.createElement("audio");
					audio.src = event.target.result;
					audio.addEventListener(
						"durationchange",
						() => {
							// check audio size and then upload file
							if (audio.duration <= Number(maxAudioDuration)) {
								const fileName = file.name.split(" ").join("_");
								const audioDuration = Math.floor(audio.duration);
								setFileName(fileName);
								setAudioDuration(audioDuration);
								props.onFileChange(url, fileName, audioDuration);
							} else {
								setIsMaxAllowedDurationError(true);
							}
						},
						false
					);

					// set null value to file upload element
					if (fileInputElement) {
						fileInputElement.value = null;
					}
				});
			} else {
				setIsFileTypeExtensionError(true);
				// set null value to file upload element
				if (fileInputElement) {
					fileInputElement.value = null;
				}
			}
		}
	};

	const playAudioFileHandler = async (fileUrl: string) => {
		if (props.templateType! === TemplateType.RequestForHelp) {
			window.open(fileUrl, "_blank");
		} else {
			try {
				const containerName = BATCH_MESSAGE_CONTAINER_NAME;
				const fileName = fileUrl.substring(fileUrl.indexOf(props.partitionKey));
				const res = await GetSASTokenForBlobFile(fileName, containerName);
				if (res.responseObj) {
					window.open(res.responseObj, "_blank");
				} else {
					Shared.showSnackbarMessage(strings.playAudioFileError);
				}
			} catch (err) {
				Shared.showSnackbarMessage(strings.playAudioFileError);
			}
		}
	};

	const deleteAudioFileAsync = async (isDeleteRecord: boolean = false) => {
		if (isDeleteRecord) {
			try {
				const containerName = IVR_FLOW_CONTAINER_NAME;
				const fileName = fileUrl.substring(fileUrl.lastIndexOf("/") + 1);
				const res = await DeleteAudioFileAsync(
					containerName,
					fileName,
					props.partitionKey,
					props.rowKey ?? ""
				);
				if (!res.error) {
					props.removeUploadedFile();
				} else {
					Shared.showSnackbarMessage(strings.removeAudioFileError);
				}
			} catch (err) {
				Shared.showSnackbarMessage(strings.removeAudioFileError);
			}
		}
		setOpenConfirmationDialog(false);
	};

	const handleRemoveUploadedFile = async () => {
		if (
			props.templateType! === TemplateType.RequestForHelp &&
			props.partitionKey &&
			fileUrl.includes(props.partitionKey)
		) {
			setOpenConfirmationDialog(true);
		} else {
			if (props.isReadOnly) {
				return;
			}
			props.removeUploadedFile();
		}
	};

	function getAudioFileDuration(duration: number) {
		return duration ? Shared.formatDuration(duration) : 0;
	}

	function getErrorMessage() {
		return isMaxAllowedDurationError
			? maxAudioDurationErrorMessage
			: isFileTypeExtensionError
			? strings.fileTypeExtensionError
			: props.highlightRequiredDetail && !fileUrl
			? strings.fileUploadValidationMessage
			: "";
	}

	return (
		<div
			className={
				props.templateType! === TemplateType.RequestForHelp
					? "uploadFileRequestForHelp"
					: "uploadFileContent"
			}
			data-testid="fileUpload"
		>
			{fileUrl && (
				<>
					{!fileUrl.includes(props.partitionKey) && fileName && (
						<>
							<Chip
								size="small"
								color="primary"
								className="newFileChip"
								label={strings.newChip}
							></Chip>
							&nbsp;
						</>
					)}
					<label className="recordingFile">
						{fileName
							? fileName
							: fileUrl.substring(fileUrl.lastIndexOf("/") + 1)}
					</label>
					<div className="fileDuration">
						{`(${strings.duration}: 
							${getAudioFileDuration(audioDuration)})`}
					</div>
					<div>
						{fileUrl.includes(props.partitionKey) && (
							<Tooltip title={strings.playAudioTooltip}>
								<span>
									<ButtonPrimary
										variant="contained"
										color="primary"
										className="playAudioBtn"
										onClick={() => playAudioFileHandler(fileUrl)}
										data-testid="playAudioBtn"
										disabled={props.isReadOnly}
									>
										<Icon>play_circle_outline</Icon>
									</ButtonPrimary>
								</span>
							</Tooltip>
						)}
						<Tooltip title={strings.removeAudioTooltip}>
							<span>
								<ButtonPrimary
									variant="contained"
									color="primary"
									className="fileUploadBtn"
									onClick={() => handleRemoveUploadedFile()}
									data-testid="removeAudioBtn"
									disabled={props.isReadOnly}
								>
									<Icon>close</Icon>
								</ButtonPrimary>
							</span>
						</Tooltip>
					</div>
				</>
			)}
			{!fileUrl && (
				<>
					<Tooltip title={strings.uploadAudioTooltip}>
						<span>
							<ButtonPrimary
								variant="contained"
								color="primary"
								onClick={() => onUploadFileButtonClick()}
								className="fileUploadBtn"
								data-testid="fileUploadBtn"
								disabled={props.isReadOnly}
							>
								<Icon>file_upload</Icon>
							</ButtonPrimary>
						</span>
					</Tooltip>
					<span>
						<b>
							({strings.noFileSelectedMessage} {maxAudioDurationInMinutes}{" "}
							{strings.minutesText})
						</b>
					</span>
					{voiceRecordingManualUrl ? (
						<span className="voiceRecordingManualText">
							{strings.clickText}{" "}
							<a
								href={voiceRecordingManualUrl}
								target="_blank"
								rel="noopener noreferrer"
							>
								{strings.hereText}
							</a>{" "}
							{strings.voiceRecordingManualText}
						</span>
					) : null}
				</>
			)}
			<input
				type="file"
				id={props.index}
				onChange={(event: any) => onFileChangeHandler(event)}
				accept={mimeTypeList.join(",")}
				className="fileInput"
			/>
			<div>
				{!fileUrl && getErrorMessage() && (
					<div className="errorMessage">{getErrorMessage()}</div>
				)}
			</div>
			<ConfirmationDialog
				open={openConfirmationDialog}
				message={`${strings.confirmRemoveAudioMessage}`}
				closeDialog={(isDeleteRecord?: boolean) =>
					deleteAudioFileAsync(isDeleteRecord)
				}
				positiveActionText={strings.positiveActionText}
				negativeActionText={strings.negativeActionText}
			></ConfirmationDialog>
		</div>
	);
});

export default FileUpload;
